import { useEffect, useState } from "react";
import Icon from "../common/Icon";
import styles from "./FloatingButton.module.css";
import { ButtonOptions } from "../common/Common";

/**
 * 지도 플로팅 버튼 설정
 * @interface
 * @prop {string} icon (optional) 버튼 아이콘 이름
 * @prop {string} label (optional) 라벨
 * @prop {{@link MouseEventHandler}<{@link HTMLElement}>} onClick (optional) 버튼 클릭 이벤트핸들러 함수
 * @prop {boolean} isActive (optional) 활성 상태 여부(false: default / true: 기능 사용중)
 * @prop {boolean} disabled (optional) 비활성화 여부(false: default / true: 클릭 불가능)
 * @prop {boolean} isLoading (optional) 로딩 상태 여부
 */
export interface FloatingBtnOptions extends ButtonOptions {
    message?: string; // TODO mjkang 버튼 툴팁 구현 시 사용
    width?: string;
    height?: string;
    isLoading?: boolean;
}

/**
 * 지도 영역 우측 지도 제어 플로팅 버튼 컴포넌트
 *
 * @author mjkang
 * @since 2024-08-23 10:02:09
 * @updated 2026-01-28 dslee - 버튼 로딩 상태 추가(버튼 위로 로딩 인디케이터 생성)
 * @param props {@link FloatingBtnOptions}
 * @returns FloatingButton
 */
const FloatingButton = (props: FloatingBtnOptions) => {
    const [isHovering, setIsHovering] = useState(false);

    // 로딩 상태가 끝날 때 호버 상태를 강제로 초기화
    useEffect(() => {
        if (props.isLoading === false) {
            setIsHovering(false);
        }
    }, [props.isLoading]);


    const handleMouseOver = () => {
        setIsHovering(true);
    };

    const handleMouseOut = () => {
        setIsHovering(false);
    };

    return props.disabled ? (
        <div className={`${styles.wrapper} ${styles.disabled}`} style={props.width ? { width: props.width } : undefined}>
            {props.icon && <Icon name={props.icon} size={"small"} color={"white"} />}
            {props.label && <span className={styles.label}>{props.label}</span>}
        </div>
    ) : (
        <div 
            className={`${styles.wrapper} ${props.isActive ? styles.active : ""}`} 
            onClick={props.onClick} onMouseOver={handleMouseOver} 
            onMouseOut={handleMouseOut} 
            style={{
                ...(props.width && { width: props.width }),
                ...(props.height && { height: props.height })
            }}
        >
            {/* 2026-01-28 | dslee | 버튼 로딩 상태 추가(버튼 위로 로딩 인디케이터 생성) */}
            {props.isLoading ? (
                <div className={styles.miniSpinner} />
            ) : (
                props.icon && <Icon name={props.icon} size={"small"} color={props.isActive ? "white" : isHovering ? "default" : "darkGrey"} />
            )}
            {props.label && <span className={`${styles.label} ${!props.isActive && isHovering ? styles.hovering : ""}`}>{props.label}</span>}
        </div>
    );
};

export default FloatingButton;
